// react
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// utils
import { reportInviteError } from 'utils/inviteError';

// mui
import { Button, Card, CardContent, Stack, Typography } from '@mui/material';

// components
import { Avatar } from '@karehero/llama/components/atoms/Avatar/Avatar';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { Logo } from '@karehero/llama/components/atoms/Logo/Logo';
import { PageTemplate } from '@karehero/llama/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';
import { Banner } from '@karehero/llama/pwa/atoms/Banner/Banner';
import DataTable, {
  GridColDef,
  GridRenderCellParams,
} from '@karehero/llama/pwa/organisms/DataTable/DataTable';

// store
import {
  useGetOrganizationAccountsQuery,
  useGetSessionOrganizationQuery,
  useReinviteMemberMutation,
} from 'store/api/organization';
import { useListOrganisationLicenceCodesQuery } from 'store/api/partnerProductCodes';
import { useSelector } from 'store';
import {
  newToast,
  dismissBanner,
  getIsBannerDismissed,
} from 'store/slices/uiSlice';

// tracking
import { TrackingEvent, useTracking } from 'hooks';

// models
import { AccountSummary, CareProfileFormEvent } from '@karehero/models';

// utils
import { getProfilePictureUrl } from '@karehero/llama/pwa/utils/getProfilePictureUrl';

// router
import { ROUTES } from 'router';

// helpers
const pluralise = (count: number, singular: string, plural: string) =>
  count === 1 ? singular : plural;

const getAssessmentDate = (
  events: CareProfileFormEvent[] | undefined,
  categoryId: string,
): string | undefined => {
  const submitted = events
    ?.filter((ev) => ev.eventType === 'form-submitted')
    ?.find((sub) => sub.careProfileCategoryId === categoryId);
  if (!submitted?.submittedAt) return undefined;
  return `Completed ${new Date(submitted.submittedAt).toLocaleDateString('en-GB')}`;
};

// columns
const columns: GridColDef[] = [
  {
    field: 'imgSrc',
    headerName: '',
    width: 72,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params: GridRenderCellParams) => {
      const row = params.row;
      let fallback =
        `${row.firstName?.[0] || ''}${row.lastName?.[0] || ''}`.toUpperCase();
      if (fallback.length === 0) {
        if (row.email && row.email.length > 0) {
          fallback = row.email[0]?.toUpperCase();
        } else {
          fallback = row.inviteKey?.[0]?.toUpperCase() || '';
        }
      }
      return (
        <Avatar size='xs' src={getProfilePictureUrl(row)} fallback={fallback} />
      );
    },
  },
  { field: 'firstName', headerName: 'First Name', flex: 1, minWidth: 120 },
  { field: 'lastName', headerName: 'Last Name', flex: 1, minWidth: 120 },
  { field: 'email', headerName: 'Email Address', flex: 1, minWidth: 200 },
  {
    field: 'careAssessment',
    headerName: 'Care Assessment',
    flex: 1,
    minWidth: 160,
    valueGetter: (_value: any, row: any) =>
      getAssessmentDate(row.careProfileFormEvents, 'care-assessment') ||
      'Incomplete',
  },
  {
    field: 'financialAssessment',
    headerName: 'Financial Assessment',
    flex: 1,
    minWidth: 170,
    valueGetter: (_value: any, row: any) =>
      getAssessmentDate(row.careProfileFormEvents, 'financial-status') ||
      'Incomplete',
  },
  {
    field: 'isOnboarded',
    headerName: 'Registered',
    flex: 0.5,
    minWidth: 100,
    valueGetter: (_value: any, row: any) => (row.isOnboarded ? 'Yes' : 'No'),
  },
];

/**
 * IFADashboardPortal wires IFADashboardPage to Redux state and API calls
 */
export const IFADashboardPortal = () => {
  // banner state
  const isBannerDismissed = useSelector((state) =>
    getIsBannerDismissed(state, 'ifa-dashboard-try-karehero'),
  );

  // hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: organization } = useGetSessionOrganizationQuery();
  const { data: accounts, isLoading: isAccountsLoading } =
    useGetOrganizationAccountsQuery(
      { organizationID: organization?.id || '', role: 'member' },
      { skip: !organization?.id },
    );
  const [reinviteMember] = useReinviteMemberMutation();
  const { data: availableLicenceCodes } = useListOrganisationLicenceCodesQuery(
    { orgId: organization?.id ?? '', available: true },
    { skip: !organization?.id },
  );
  const { track } = useTracking();

  useEffect(() => {
    track(TrackingEvent.InternalPartnerReferralFormViewed, {});
  }, [track]);

  // memos
  const rows = useMemo(
    () => (accounts || []).map((a) => ({ ...a })),
    [accounts],
  );

  const isEmpty = !isAccountsLoading && (accounts?.length ?? 0) === 0;

  const infoCards = useMemo(() => {
    if (!accounts) return [];
    const active = accounts.filter((a) => a.isOnboarded).length;
    const pending = accounts.filter((a) => !a.isOnboarded).length;
    const cards: { value: string; label: string }[] = [
      {
        value: String(active),
        label: pluralise(active, 'active member', 'active members'),
      },
      {
        value: String(pending),
        label: pluralise(pending, 'pending invite', 'pending invites'),
      },
    ];
    if (availableLicenceCodes && availableLicenceCodes.length > 0) {
      const count = availableLicenceCodes.length;
      cards.push({
        value: String(count),
        label: `${pluralise(count, 'seat', 'seats')} available`,
      });
    }
    return cards;
  }, [accounts, availableLicenceCodes]);

  return (
    <Stack>
      {!isBannerDismissed && (
        <Banner
          title='Try KareHero Yourself First'
          description='Many partners find the service valuable for their own family before recommending it to clients. Experience the care journey personally. It only takes 5 minutes.'
          icon='user'
          onDismiss={() =>
            dispatch(dismissBanner('ifa-dashboard-try-karehero'))
          }
          action={{
            label: 'Get Started',
            onClick: () => navigate(ROUTES.PORTAL_SELF_REFERRAL),
          }}
        />
      )}

      {isEmpty ? (
        <PageTemplate
          maxWidth={false}
          contentSlot={
            <Stack
              spacing={3}
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
                textAlign: 'center',
                px: 3,
              }}
            >
              <Logo variant='symbol' size='lg' color='primary' />
              <Typography variant='h4' sx={{ color: 'text.title' }}>
                Welcome to KareHero
              </Typography>
              <Typography
                variant='body1'
                sx={{
                  color: 'text.primary',
                  maxWidth: 600,
                }}
              >
                KareHero is a care concierge for your clients' families. We help
                with everything from finding carers to claiming benefits like
                Attendance Allowance, so they're supported long before care is
                needed.
              </Typography>
              <Button
                variant='contained'
                onClick={() => navigate(ROUTES.PORTAL_CLIENT_REFERRAL)}
              >
                Invite Client
              </Button>
            </Stack>
          }
        />
      ) : (
        <PageTemplate
          maxWidth={false}
          headerSlot={
            <Stack spacing={2}>
              <PageHeader
                title={organization?.name || ''}
                description='Invite a client to give them access to KareHero. Inviting clients sooner means they can be prepared and informed ahead of needing our services.'
              />
              <Stack direction='row' spacing={2} sx={{ width: '100%' }}>
                {infoCards.map((card, index) => (
                  <Card key={index} sx={{ flex: 1 }}>
                    <CardContent>
                      <Stack spacing={1}>
                        <Typography variant='h2'>{card.value}</Typography>
                        <Typography
                          variant='body2'
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          {card.label}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Stack>
          }
          contentSlot={
            <DataTable
              columns={columns}
              rows={rows}
              actions={[
                {
                  label: 'Reinvite',
                  icon: 'envelope',
                  isHidden: (row: AccountSummary) =>
                    !!row.isOnboarded || !row.email,
                  onPress: async (row: AccountSummary) => {
                    if (!row.id) return;
                    try {
                      await reinviteMember(row.id).unwrap();
                      dispatch(
                        newToast({
                          title: 'Invitation Success',
                          description: `Resent invitation to ${row.email || 'the user'}`,
                        }),
                      );
                    } catch (e) {
                      reportInviteError(e);
                    }
                  },
                },
              ]}
              toolbarActions={
                <Stack direction='row' spacing={1}>
                  <Button
                    variant='outlined'
                    size='small'
                    startIcon={<Icon icon='user' size='sm' />}
                    onClick={() => navigate(ROUTES.PORTAL_SELF_REFERRAL)}
                  >
                    Invite Yourself As End User
                  </Button>
                  <Button
                    variant='outlined'
                    size='small'
                    startIcon={<Icon icon='shield-plus' size='sm' />}
                    onClick={() => navigate(ROUTES.PORTAL_ADMIN_INVITE)}
                  >
                    Add Organisation Admin
                  </Button>
                  <Button
                    variant='contained'
                    size='small'
                    startIcon={<Icon icon='plus' size='sm' />}
                    onClick={() => navigate(ROUTES.PORTAL_CLIENT_REFERRAL)}
                  >
                    Invite Client
                  </Button>
                </Stack>
              }
            />
          }
        />
      )}
    </Stack>
  );
};

export default IFADashboardPortal;
